import { useEffect, useState } from 'react'
import { useApp } from '../../context/AppContext'

/**
 * Componente para la gestión y selección de etiquetas (tags) de tickets.
 * * Ofrece una interfaz de búsqueda en tiempo real que permite:
 * 1. Seleccionar etiquetas existentes desde la base de datos.
 * 2. Crear nuevas etiquetas dinámicamente si no existen.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.onAdd - Callback ejecutado cuando se selecciona o crea una etiqueta.
 * Recibe el objeto completo de la etiqueta (id, name).
 * * @returns {JSX.Element} Un buscador de etiquetas con autocompletado y creación dinámica.
 */
const TagTicketAdd = ({ onAdd }) => {
  /** @type {Object} Cliente de Supabase extraído del contexto global. */
  const { supabase } = useApp()

  /** @type {[Array<Object>, Function]} Lista maestra de etiquetas obtenidas de la DB. */
  const [tags, setTags] = useState([])

  /** @type {[string, Function]} Valor actual de la búsqueda e input de creación. */
  const [search, setSearch] = useState('')

  /** @type {[boolean, Function]} Estado de carga para la inserción de nuevas etiquetas. */
  const [loading, setLoading] = useState(false)

  /**
   * Obtiene la lista completa de etiquetas desde el esquema 'Tickets'.
   * Las etiquetas se ordenan alfabéticamente por nombre.
   * @async
   * @function getTags
   */
  async function getTags() {
    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('tag')
        .select('*')
        .order('name', { ascending: true })

      if (error) throw error
      setTags(data || [])
    } catch (error) {
      console.error('Error al obtener tags:', error)
    }
  }

  /**
   * Efecto inicial para cargar las etiquetas al montar el componente.
   */
  useEffect(() => {
    getTags()
  }, [])

  /** * Filtra la lista de etiquetas basándose en el término de búsqueda actual.
   * No distingue entre mayúsculas y minúsculas.
   */
  const filteredTags = tags.filter((tag) =>
    tag.name.toLowerCase().includes(search.toLowerCase())
  )

  /** * Verifica si el término buscado coincide exactamente con alguna etiqueta existente.
   * Se utiliza para mostrar u ocultar la opción de "Crear nueva etiqueta".
   */
  const tagExists = tags.some(
    (tag) => tag.name.toLowerCase() === search.toLowerCase()
  )

  /**
   * Crea una nueva etiqueta en la base de datos y la añade al estado local.
   * Tras la creación, invoca el callback `onAdd` con el nuevo registro.
   * @async
   * @function handleCreateTag
   */
  const handleCreateTag = async () => {
    if (!search.trim()) return
    setLoading(true)
    try {
      const { data, error } = await supabase
        .schema('Tickets')
        .from('tag')
        .insert([{ name: search.trim() }])
        .select()
        .single()

      if (error) throw error

      setTags([...tags, data])
      onAdd(data)
      setSearch('')
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="position-relative w-100">
      {/* Input de búsqueda estilizado */}
      <div className="input-group input-group-sm shadow-sm rounded-3 overflow-hidden border">
        <span className="input-group-text bg-body-secondary border-0 text-secondary">
          <i className="fas fa-tag"></i>
        </span>
        <input
          type="text"
          className="form-control bg-body border-0 shadow-none py-2"
          placeholder="Buscar o crear etiqueta..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ color: 'inherit' }}
        />
        {search && (
          <button
            className="btn bg-body border-0 text-danger"
            onClick={() => setSearch('')}
          >
            <i className="fas fa-times"></i>
          </button>
        )}
      </div>

      {/* Resultados desplegables */}
      {search && (
        <div
          className="position-absolute w-100 mt-1 shadow-lg rounded-3 overflow-hidden border z-3 bg-body"
          style={{ maxHeight: '250px', overflowY: 'auto' }}
        >
          <div className="list-group list-group-flush">
            {filteredTags.map((tag) => (
              <button
                key={tag.id}
                className="list-group-item list-group-item-action border-0 d-flex align-items-center py-2 px-3 transition-all"
                onClick={() => {
                  onAdd(tag)
                  setSearch('')
                }}
              >
                <i className="fas fa-hashtag me-2 text-primary opacity-50"></i>
                <span className="flex-grow-1">{tag.name}</span>
                <i className="fas fa-chevron-right small opacity-25"></i>
              </button>
            ))}

            {/* Opción de creación dinámica */}
            {!tagExists && (
              <button
                className="list-group-item list-group-item-action border-0 py-2 px-3 bg-primary-subtle text-primary-emphasis fw-medium"
                onClick={handleCreateTag}
                disabled={loading}
              >
                <div className="d-flex align-items-center">
                  <i
                    className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-plus-circle'} me-2`}
                  ></i>
                  <span>Crear {search}</span>
                </div>
              </button>
            )}

            {filteredTags.length === 0 && tagExists && (
              <div className="list-group-item small text-muted text-center py-3">
                No hay resultados
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        /* Adaptación manual para Dark Mode si no usas los temas de Bootstrap 5.3 */
        [data-bs-theme='dark'] .bg-body {
          background-color: var(--text) !important;
        }
        
        .transition-all {
          transition: all 0.2s ease;
        }

        .list-group-item-action:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.1);
          transform: translateX(4px);
        }

        /* Ocultar scrollbar pero mantener funcionalidad */
        ::-webkit-scrollbar {
          width: 5px;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,0.2);
          border-radius: 10px;
        }
      `}</style>
    </div>
  )
}

export default TagTicketAdd
